



































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { EntityTypes } from '@/helpers/entityType';
import BookingFormOpenedGaEvent from '@/helpers/ga/events/BookingFormOpenedGaEvent';
import BookingFormSubmittedGaEvent from '@/helpers/ga/events/BookingFormSubmittedGaEvent';
import { getContentTypeFromEntityType } from '@/helpers/ga/params/contentTypeParam';
import { sendGaEvent } from '@/helpers/ga/gaSend';
import { EXPERIENCE_BOOKING_TYPE } from '@/helpers/experience';

@Component({
  components: {
    DownloadMobileAppModal: () => import('@/components/modals/DownloadMobileAppModal.vue'),
    TheAwaitingConfirmationIcon: () => import('@/components/icons/TheAwaitingConfirmationIcon.vue'),
    TheConfirmedIcon: () => import('@/components/icons/TheConfirmedIcon.vue'),
    TheNotConfirmedIcon: () => import('@/components/icons/TheNotConfirmedIcon.vue'),
    TheBookTourIcon: () => import('@/components/icons/TheBookTourIcon.vue'),
    ThePlusCircleIcon: () => import('@/components/icons/ThePlusCircleIcon.vue'),
    TheTicketIcon: () => import('@/components/icons/TheTicketIcon.vue'),
    TourBookingForm: () => import('@/components/partials/TourBookingForm.vue'),
    TicketRequest: () => import('@/components/partials/TicketRequest.vue'),
    AppJoinWaitlistForm: () => import('@/components/AppJoinWaitlistForm.vue'),
  },
})
export default class TicketRequestAction extends Vue {
  @Prop() event!: any;
  @Prop() labelClass: string;
  @Prop() object!: String;
  @Prop() objectId!: Number;
  @Prop({ default: false, type: Boolean }) selectFromExhibitions: Boolean;
  @Prop({ default: false, type: Boolean }) show: Boolean;
  @Prop({ default: false, type: Boolean }) tour: Boolean;
  @Prop({ default: 'data-details__action' }) wrapperClass: string;

  isLoader = false;
  requestedBooking: any = null;
  showDownloadMobileAppModal = false;
  showForm = false;

  get accessType() {
    return this.event.accessType || { id: null, name: '' };
  }

  get actionDisabled() {
    return !!this.activeBooking;
  }

  get actionIconComponent() {
    if (this.tour || this.isExperienceEvent) {
      return 'TheBookTourIcon';
    }

    if (this.bookAcademySession) {
      return 'ThePlusCircleIcon';
    }

    return 'TheTicketIcon';
  }

  get actionLabel() {
    if (this.activeBooking) {
      return this.activeBooking.status_for_human;
    }

    if (this.actionDisabled) {
      return '';
    }

    if (this.tour) {
      return 'Request a Tour';
    }

    if (this.bookAcademySession) {
      return 'Book a Session';
    }

    if (this.accessType.id) {
      return this.accessType.name;
    }

    if (!this.availableForBooking) {
      return 'Join the Waitlist';
    }

    if (this.isExperienceEvent) {
      return this.event.bookingType === EXPERIENCE_BOOKING_TYPE.onDemand ? 'Book Now' : 'Buy Now';
    }

    return '';
  }

  get activeBooking() {
    let activeBooking = null;

    if (this.tour && this.event.activeTourBooking) {
      activeBooking = this.event.activeTourBooking;
    } else if (!this.tour && this.event.activeBooking) {
      activeBooking = this.event.activeBooking;
    }

    if (activeBooking === null && this.requestedBooking) {
      return this.requestedBooking;
    }

    return activeBooking;
  }

  get bookAcademySession() {
    return this.event.__typename === EntityTypes.academy && this.event.based_type !== 'Scheduled';
  }

  get isExhibitionEvent() {
    return this.event.__typename === EntityTypes.exhibition;
  }

  get isExperienceEvent() {
    return this.event.__typename === EntityTypes.experience;
  }

  get availableForBooking() {
    return this.isExperienceEvent ? this.event.availableForBooking : true;
  }

  get useDateTimeStep() {
    return this.isExperienceEvent ? this.event.bookingType === EXPERIENCE_BOOKING_TYPE.onDemand : true;
  }

  get isTourBookingForMuseumExhibition() {
    return (
      this.tour &&
      [EntityTypes.art_space, EntityTypes.exhibition, EntityTypes.historical_site].includes(this.event.__typename)
    );
  }

  get exhibitions() {
    return this.selectFromExhibitions ? this.event.exhibitions || [] : [];
  }

  get disableTicketBooking() {
    if (this.tour) {
      return false;
    }

    if (this.isExhibitionEvent && !this.event.is_permanent_collection) {
      return false;
    }

    return !!this.event.artSpace ? !!this.event.artSpace.generalTicketProduct : !!this.event.generalTicketProduct;
  }

  get isCreateMode() {
    return !this.activeBooking?.id;
  }

  get showAction() {
    return this.actionDisabled || (!this.event.is_expired && !this.event.fully_booked);
  }

  get gaBookingType() {
    return this.isTourBookingForMuseumExhibition ? 'guided_tour' : this.event.bookingFormType;
  }

  @Watch('show', { immediate: true })
  watchShow(newValue: boolean) {
    if (newValue) {
      this.open(true);
    }
  }

  handleRequested(booking?: any) {
    const ctaName = this.actionLabel;

    this.requestedBooking = {
      id: booking?.id || '',
      is_awaiting_status: booking?.is_awaiting_status || true,
      is_confirmed_status: booking?.is_confirmed_status || false,
      is_not_confirmed_status: booking?.is_not_confirmed_status || false,
      status_for_human: booking?.status_for_human || 'Awaiting confirmation',
    };

    const gaEvent = new BookingFormSubmittedGaEvent()
      .addBookingType(this.gaBookingType)
      .addContentType(getContentTypeFromEntityType(this.event.__typename))
      .addContentId(this.event.id)
      .addContentName(this.event.title)
      .addCtaName(ctaName)
      .addPartnerName(this.event.corporatePartner);

    sendGaEvent(gaEvent);

    this.$emit('requested');
  }

  handleOpen() {
    if (this.actionDisabled) {
      return;
    }

    if (this.disableTicketBooking) {
      this.showDownloadMobileAppModal = true;
      return;
    }

    this.open();
  }

  open(ignoreDisabled?: boolean) {
    if (!ignoreDisabled && this.actionDisabled) {
      return;
    }
    this.showForm = true;
    this.$store.dispatch('disableScroll');

    const gaEvent = new BookingFormOpenedGaEvent()
      .addBookingType(this.gaBookingType)
      .addContentType(getContentTypeFromEntityType(this.event.__typename))
      .addContentId(this.event.id)
      .addContentName(this.event.title)
      .addCtaName(this.actionLabel)
      .addPartnerName(this.event.corporatePartner);

    sendGaEvent(gaEvent);
  }

  close() {
    this.showForm = false;
    this.$emit('close');
    this.$store.dispatch('enableScroll');
  }

  closeByOverlay() {
    if (this.isCreateMode) {
      this.close();
    }
  }
}
